import { AngleUtils } from '../../utils/AngleUtils/AngleUtils';

export class HtmlVector {
  private readonly x: number;
  private readonly y: number;

  constructor(x: number, y: number) {
    this.x = x !== 0 ? x : 0; // convert -0 to 0
    this.y = y !== 0 ? y : 0; // convert -0 to 0
  }

  public getX(): number {
    return this.x;
  }

  public setX(x: number): HtmlVector {
    return new HtmlVector(x, this.y);
  }

  public getY(): number {
    return this.y;
  }

  public setY(y: number): HtmlVector {
    return new HtmlVector(this.x, y);
  }

  public getLength(): number {
    return Math.sqrt(Math.pow(this.x, 2) + Math.pow(this.y, 2));
  }

  /**
   * @param angle - in degrees
   */
  public rotate(angle: number): HtmlVector {
    const currentAngle = this.getAngle();
    if (currentAngle == null) {
      return this;
    }

    return this.setAngle(currentAngle + angle);
  }

  public getAngle(): number | null {
    const xCalc = this.x;
    const yCalc = this.y * -1;

    if (xCalc === 0) {
      if (yCalc === 0) {
        return null;
      }

      return yCalc > 0 ? 90 : 270;
    }

    if (yCalc === 0) {
      return xCalc > 0 ? 0 : 180;
    }

    const angle = AngleUtils.radToDeg(Math.atan(yCalc / xCalc));

    if (xCalc < 0 && yCalc > 0) {
      //quadrant 2
      return angle + 180;
    } else if (xCalc < 0 && yCalc < 0) {
      //quadrant 3
      return angle + 180;
    } else if (angle < 0) {
      //quadrant 4
      return angle + 360;
    }

    return angle;
  }

  /**
   * @param angle - in degrees
   */
  public setAngle(angle: number): HtmlVector {
    const length = this.getLength();

    return new HtmlVector(
      Math.cos(AngleUtils.degToRad(angle)) * length,
      Math.sin(AngleUtils.degToRad(angle)) * length * -1
    );
  }

  public addVector(vector: HtmlVector): HtmlVector {
    return this.addXY(vector.x, vector.y);
  }

  public addXY(x: number, y: number): HtmlVector {
    return new HtmlVector(this.x + x, this.y + y);
  }

  public substractVector(vector: HtmlVector): HtmlVector {
    return this.subsctractXY(vector.x, vector.y);
  }

  public subsctractXY(x: number, y: number): HtmlVector {
    return new HtmlVector(this.x - x, this.y - y);
  }

  public multiplyVector(vector: HtmlVector): HtmlVector {
    return this.multiplyXY(vector.x, vector.y);
  }

  public multiplyXY(x: number, y: number): HtmlVector {
    return new HtmlVector(this.x * x, this.y * y);
  }

  public divideVector(vector: HtmlVector): HtmlVector {
    return this.divideXY(vector.x, vector.y);
  }

  public divideXY(x: number, y: number): HtmlVector {
    return new HtmlVector(this.x / x, this.y / y);
  }

  public toJSON(): { x: number; y: number } {
    return {
      x: this.x,
      y: this.y
    };
  }
}
